import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from "recharts";
import ChartContainer from "@client.components/ChartContainer";
import useAvailableColors from "@client.hooks/useAvailableColors";
import useModal from "@client.hooks/useModal";
import { makeProfile } from "@client.pages/Login/reducer";
import Pages from "@client.enums/pages";
import { timeSpentColorIndexes } from "@client.enums/meetingTypeColorIndexes";
import { getTimeSpentAsync, makeTimeSpent } from "../reducer";
import CustomTooltip from "../CustomTooltip";
import { filterPropTypes } from "../utils";
import { getTimeSpentDetailedInformation } from "../consts";
import MeetingDetailsContent from "../ChartWithDetailedTooltip/MeetingDetailsContent";
import "./TimeSpent.scss";

const TimeSpent = ({ filter, onError }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showModal } = useModal();

  const timeSpent = useSelector(makeTimeSpent);
  const profile = useSelector(makeProfile);
  const dispatch = useDispatch();
  const [getSubsequentColor] = useAvailableColors();

  if (timeSpent.error) {
    onError(timeSpent.error);
  }

  useEffect(() => {
    dispatch(
      getTimeSpentAsync({
        ...filter,
        isCostIndexPresentation: profile.isCostIndexPresentation,
      })
    );
  }, [dispatch, filter, profile]);

  const data = timeSpent.data.map((d) => ({ ...d, name: t(d.name) }));

  const handleCellClick = (data) => {
    if (!data?.meetings?.length) return;

    const { tooltip, details } = getTimeSpentDetailedInformation(data, t);

    showModal({
      show: true,
      title: tooltip,
      component: (
        <MeetingDetailsContent meetings={data.meetings} details={details} />
      ),
      contentClass: "meeting-tooltip-details-content",
      footerButtons: [
        {
          icon: "close",
          text: t("components.Modal.FooterButtons.close"),
          onClick: () => Promise.resolve({}),
        },
        {
          icon: "reports",
          text: t("components.Modal.FooterButtons.goToReport"),
          onClick: () =>
            Promise.resolve({}).then(() => {
              navigate(Pages.ReportsMeetings);
              return {};
            }),
        },
      ],
    });
  };

  const renderCustomLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    name,
  }) => {
    const RADIAN = Math.PI / 180;
    const radius = 25 + innerRadius + (outerRadius - innerRadius);
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        className="pie-label"
      >
        {name}
      </text>
    );
  };
  const totalSum = timeSpent.load
    ? 0
    : timeSpent.data.reduce((a, v) => a + v.value, 0);
  return (
    <ChartContainer
      className="time-spent"
      title={t("components.Charts.internalExternal")}
      isLoading={timeSpent.load}
      data={data}
      isDataAvailable={(d) => d.filter((r) => r.value > 0).length > 0}
    >
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Tooltip
            content={<CustomTooltip isSingleValue={true} totalSum={totalSum} />}
          />
          <Pie
            data={data.filter((r) => r.value > 0)}
            innerRadius={60}
            outerRadius={80}
            paddingAngle={5}
            dataKey="value"
            labelLine={false}
            label={renderCustomLabel}
          >
            {timeSpent.data.map((entry, index) => {
              return (
                <Cell
                  fill={getSubsequentColor(timeSpentColorIndexes[entry.name])}
                  key={`ts-${index}`}
                  className="pie-cell"
                  onClick={() => handleCellClick(entry)}
                />
              );
            })}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

TimeSpent.propTypes = {
  onError: PropTypes.func.isRequired,
  filter: filterPropTypes,
};

export default TimeSpent;
